"use client";

import { useCallback, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import {
  changeLocale,
  getLocalePreference,
  normalizeLocale,
  type LocaleCode,
  type LocalePreference,
} from "@/i18n";

/** Compact glass language toggle styled for the bioluminescent scene. */
export function SceneLanguageToggle() {
  const { i18n } = useTranslation();

  const subscribe = useCallback(
    (sync: () => void) => {
      i18n.on("languageChanged", sync);
      window.addEventListener("eazo-locale-preference-changed", sync);
      window.addEventListener("storage", sync);
      return () => {
        i18n.off("languageChanged", sync);
        window.removeEventListener("eazo-locale-preference-changed", sync);
        window.removeEventListener("storage", sync);
      };
    },
    [i18n],
  );

  useSyncExternalStore(
    subscribe,
    getLocalePreference,
    () => "system" as LocalePreference,
  );

  const active = normalizeLocale(i18n.resolvedLanguage || i18n.language) ?? "en-US";
  const next: LocaleCode = active === "zh-CN" ? "en-US" : "zh-CN";

  return (
    <button
      type="button"
      onClick={() => void changeLocale(next)}
      data-el="scene-language-toggle"
      className="grid h-11 w-11 place-items-center rounded-full text-[13px] font-medium text-teal active:scale-95"
      style={{
        border: "1px solid var(--scene-border)",
        background: "rgba(7,18,14,.48)",
        backdropFilter: "blur(16px)",
        boxShadow: "0 0 22px rgba(126,216,176,.16)",
      }}
      aria-label={active === "zh-CN" ? "Switch to English" : "切换为中文"}
    >
      {active === "zh-CN" ? "EN" : "中"}
    </button>
  );
}
